import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { ArrowUpRight, Copy, Check, ChevronDown, Loader2, Wallet, Ban } from "lucide-react";
import { useState, useEffect } from "react";
import { cancelTransaction } from "@/utils/charge";
import { Transaction, TRANSACTION_TYPES, Membership, EntryPass, TransactionBreakdown } from "@/constants";
import { formatShortCryptoAddress } from "@/utils/format";
import { useToast } from "@/hooks/use-toast";
import { posApiFetch } from "@/utils/pos-api";
import CardBrandIcon from "@/components/CardBrandIcon";

interface AdminTransactionDetailDialogProps {
  transaction: Transaction | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRefund?: () => void;
}

// ─── Helpers ────────────────────────────────────────────────

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const GUEST_TYPE_LABELS: Record<string, string> = {
  couples: "Couples",
  single_ladies: "Single Ladies",
  single_males: "Single Males",
};

function formatGuestType(type?: string | null) {
  if (!type) return null;
  return GUEST_TYPE_LABELS[type] ?? type;
}

function formatDateRange(start: string, end: string) {
  const s = new Date(start);
  const e = new Date(end);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
  return `${s.toLocaleDateString("en-US", opts)} – ${e.toLocaleDateString("en-US", opts)}`;
}

function formatTimeSlot(slot?: string) {
  if (!slot) return null;
  return slot === "daytime" ? "Daytime" : slot === "evening" ? "Evening" : slot;
}

// ─── Detail row (light theme) ───────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null;
  return (
    <div className="flex justify-between items-start gap-3 py-1.5">
      <span className="text-[var(--secondary-text-color)] text-[12px] shrink-0">{label}</span>
      <span className="text-[var(--primary-text-color)] text-[12px] font-medium text-right">{value}</span>
    </div>
  );
}

// ─── Status badge (light theme) ─────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    inactive: "bg-gray-100 text-gray-600",
    expired: "bg-red-100 text-red-700",
    cancelled: "bg-red-100 text-red-700",
  };
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${colors[status] ?? "bg-gray-100 text-gray-600"}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

// ─── Collapsible section wrapper (light theme) ──────────────

function RelatedSection({
  title,
  isLoading,
  children,
}: {
  title: string;
  isLoading: boolean;
  children: React.ReactNode;
}) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="w-full flex items-center justify-between px-5 py-3 border-t border-gray-200 hover:bg-gray-50 transition-colors group">
        <span className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider">
          {title}
        </span>
        {isLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--secondary-text-color)]" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--secondary-text-color)] transition-transform group-data-[state=open]:rotate-180" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-5 pb-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-[var(--secondary-text-color)]" />
            </div>
          ) : (
            children
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// ─── Transaction breakdown rows (light theme) ───────────────

function fmtAmount(value: string | number, isSech: boolean) {
  const n = Number(value);
  return isSech
    ? `${n.toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
    : `$${n.toFixed(2)}`;
}

function BreakdownRows({ tx }: { tx: TransactionBreakdown }) {
  const isSech = tx.currency === "SECH";

  return (
    <div className="space-y-2 text-sm pt-2">
      {tx.subtotal && (
        <div className="flex justify-between">
          <span className="text-[var(--secondary-text-color)]">Subtotal</span>
          <span className="text-[var(--primary-text-color)]">{fmtAmount(tx.subtotal, isSech)}</span>
        </div>
      )}
      {tx.discountAmount && (
        <div className="flex justify-between text-green-600">
          <span>Discount</span>
          <span>-{fmtAmount(tx.discountAmount, isSech)}</span>
        </div>
      )}
      <div className="border-t border-dashed border-gray-300 my-2" />
      {tx.taxBreakdown && tx.taxBreakdown.length > 0 && (
        <div className="flex justify-between">
          <span className="text-[var(--secondary-text-color)]">Taxes & Fees</span>
          <span className="text-[var(--primary-text-color)]">
            {isSech
              ? `${tx.taxBreakdown.reduce((sum, t) => sum + t.amount, 0).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
              : `$${tx.taxBreakdown.reduce((sum, t) => sum + t.amountUSD, 0).toFixed(2)}`}
          </span>
        </div>
      )}
      <div className="flex justify-between font-semibold">
        <span className="text-[var(--primary-text-color)]">Total</span>
        <span className="text-[var(--primary-text-color)]">{fmtAmount(
          isSech ? (tx.totalSech ?? "0") : (tx.totalUsd ?? "0"),
          isSech
        )}</span>
      </div>
    </div>
  );
}

// ─── Membership details (admin, uses posApiFetch) ───────────

function AdminMembershipDetails({ transactionId, walletAddress }: { transactionId: number; walletAddress: string }) {
  const [membership, setMembership] = useState<Membership | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    function parseMemberships(json: any): Membership[] {
      if (Array.isArray(json)) return json;
      if (json?.data?.items && Array.isArray(json.data.items)) return json.data.items;
      if (Array.isArray(json?.data)) return json.data;
      return [];
    }

    async function fetchData() {
      try {
        setIsLoading(true);

        // Try wallet-address-scoped endpoint first (matches entry-pass pattern)
        const walletRes = await posApiFetch(`/v1/memberships/${walletAddress}`);
        if (walletRes.ok) {
          const walletData = await walletRes.json();
          const memberships = parseMemberships(walletData);
          const match = memberships.find((m) => m.transactionId === transactionId);
          if (match) {
            if (!cancelled) setMembership(match);
            return;
          }
        }

        // Fallback: fetch all memberships
        const res = await posApiFetch("/v1/memberships");
        if (!res.ok) throw new Error("Failed to fetch memberships");
        const data = await res.json();
        const memberships = parseMemberships(data);
        const match = memberships.find((m) => m.transactionId === transactionId);
        if (!cancelled) setMembership(match ?? null);
      } catch {
        if (!cancelled) setMembership(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
  }, [transactionId, walletAddress]);

  return (
    <RelatedSection title="Membership Details" isLoading={isLoading}>
      {membership ? (
        <div className="divide-y divide-gray-100">
          <DetailRow label="Status" value={<StatusBadge status={membership.status} />} />
          {membership.templateName && <DetailRow label="Plan" value={membership.templateName} />}
          {membership.guestType && <DetailRow label="Guest Type" value={formatGuestType(membership.guestType)} />}
          {membership.durationMonths && <DetailRow label="Duration" value={`${membership.durationMonths} month${membership.durationMonths > 1 ? "s" : ""}`} />}
          <DetailRow label="Period" value={formatDateRange(membership.startDate, membership.endDate)} />
          {membership.transaction ? (
            <BreakdownRows tx={membership.transaction} />
          ) : membership.price ? (
            <DetailRow label="Price" value={`$${Number(membership.price).toFixed(2)}`} />
          ) : null}
          {membership.primaryName && <DetailRow label="Primary" value={membership.primaryName} />}
          {membership.partnerName && <DetailRow label="Partner" value={membership.partnerName} />}
        </div>
      ) : (
        <p className="text-[var(--secondary-text-color)] text-[12px] py-1">No membership data found for this transaction.</p>
      )}
    </RelatedSection>
  );
}

// ─── Entry pass details (admin) ─────────────────────────────

function AdminEntryPassDetails({ transactionId, walletAddress }: { transactionId: number; walletAddress: string }) {
  const [entryPass, setEntryPass] = useState<EntryPass | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      try {
        setIsLoading(true);
        const res = await posApiFetch(`/v1/entry-passes/${walletAddress}/history`);
        if (res.status === 404) {
          if (!cancelled) setEntryPass(null);
          return;
        }
        if (!res.ok) throw new Error("Failed to fetch entry passes");
        const data: EntryPass[] = await res.json();
        const match = data.find((p) => p.transactionId === transactionId);
        if (!cancelled) setEntryPass(match ?? null);
      } catch {
        if (!cancelled) setEntryPass(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [transactionId, walletAddress]);

  return (
    <RelatedSection title="Entry Pass Details" isLoading={isLoading}>
      {entryPass ? (
        <div className="divide-y divide-gray-100">
          <DetailRow label="Status" value={<StatusBadge status={entryPass.status} />} />
          {entryPass.templateName && <DetailRow label="Pass" value={entryPass.templateName} />}
          {entryPass.guestType && <DetailRow label="Guest Type" value={formatGuestType(entryPass.guestType)} />}
          {entryPass.dayOfWeek !== undefined && entryPass.dayOfWeek !== null && (
            <DetailRow label="Day" value={DAYS_OF_WEEK[entryPass.dayOfWeek] ?? `Day ${entryPass.dayOfWeek}`} />
          )}
          {entryPass.timeSlot && <DetailRow label="Time Slot" value={formatTimeSlot(entryPass.timeSlot)} />}
          {entryPass.startTime && entryPass.endTime && <DetailRow label="Hours" value={`${entryPass.startTime} – ${entryPass.endTime}`} />}
          {entryPass.transaction ? (
            <BreakdownRows tx={entryPass.transaction} />
          ) : entryPass.price ? (
            <DetailRow label="Price" value={`$${Number(entryPass.price).toFixed(2)}`} />
          ) : null}
          {entryPass.validFor && (
            <DetailRow label="Valid Until" value={new Date(entryPass.validFor).toLocaleDateString("en-US", {
              weekday: "short", year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
            })} />
          )}
        </div>
      ) : (
        <p className="text-[var(--secondary-text-color)] text-[12px] py-1">No entry pass data found for this transaction.</p>
      )}
    </RelatedSection>
  );
}

// ─── Order details (admin) ──────────────────────────────────

interface OrderData {
  id: number;
  orderNumber: string;
  status: string;
  totalUsd: string;
  totalSech: string;
  subtotal?: string | null;
  subtotalSech?: string | null;
  taxAmount?: string | null;
  taxBreakdown?: Array<{
    rate: number;
    type: string;
    amount: number;
    amountUSD: number;
    description: string;
  }> | null;
  discountAmount?: string | null;
  discountAmountSech?: string | null;
  discountDetails?: {
    discountName?: string;
    discountType?: string;
    discountValue?: number;
    appliedAmount?: number;
  } | null;
  paymentMethod?: "CARD" | "TOKEN" | null;
  createdAt: string;
  paymentHash?: string | null;
  notes?: string | null;
  items?: Array<{
    id: number;
    quantity: number;
    unitPriceUsd: string;
    unitPriceSech: string;
    product?: { name: string } | null;
    customDrink?: { name: string } | null;
  }>;
}

function AdminOrderDetails({ orderId, isCard }: { orderId: string; isCard: boolean }) {
  const [order, setOrder] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      try {
        setIsLoading(true);
        const res = await posApiFetch("/v1/pos/orders");
        if (!res.ok) throw new Error("Failed to fetch orders");
        const data: OrderData[] = await res.json();
        const match = data.find(
          (o) => o.orderNumber === orderId || String(o.id) === orderId || o.paymentHash === orderId
        );
        if (!cancelled) setOrder(match ?? null);
      } catch {
        if (!cancelled) setOrder(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetch();
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <RelatedSection title="Order Details" isLoading={isLoading}>
      {order ? (
        <div className="divide-y divide-gray-100">
          <DetailRow label="Order #" value={order.orderNumber} />
          <DetailRow label="Status" value={<StatusBadge status={order.status} />} />
          {order.items && order.items.length > 0 && (
            <div className="pt-2 pb-1">
              <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1.5">Items</p>
              <div className="flex flex-col gap-1">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center">
                    <span className="text-[var(--primary-text-color)] text-[12px]">
                      {item.product?.name ?? item.customDrink?.name ?? "Unknown Item"} &times; {item.quantity}
                    </span>
                    <span className="text-[var(--secondary-text-color)] text-[12px]">
                      {isCard
                        ? `$${(Number(item.unitPriceUsd) * item.quantity).toFixed(2)}`
                        : `${(Number(item.unitPriceSech) * item.quantity).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2 text-sm pt-2">
            {order.subtotal && (
              <div className="flex justify-between">
                <span className="text-[var(--secondary-text-color)]">Subtotal</span>
                <span className="text-[var(--primary-text-color)]">{isCard
                  ? `$${Number(order.subtotal).toFixed(2)}`
                  : order.subtotalSech
                    ? `${Number(order.subtotalSech).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
                    : undefined
                }</span>
              </div>
            )}
            {order.discountDetails && order.discountAmount && (
              <div className="flex justify-between text-green-600">
                <span>Discount</span>
                <span>{isCard
                  ? `-$${Number(order.discountAmount).toFixed(2)}`
                  : order.discountAmountSech
                    ? `-${Number(order.discountAmountSech).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
                    : undefined
                }</span>
              </div>
            )}
            <div className="border-t border-dashed border-gray-300 my-2" />
            {order.taxBreakdown && order.taxBreakdown.length > 0 && (
              <div className="flex justify-between">
                <span className="text-[var(--secondary-text-color)]">Taxes & Fees</span>
                <span className="text-[var(--primary-text-color)]">{isCard
                  ? `$${order.taxBreakdown.reduce((sum, t) => sum + t.amountUSD, 0).toFixed(2)}`
                  : `${order.taxBreakdown.reduce((sum, t) => sum + t.amount, 0).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
                }</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span className="text-[var(--primary-text-color)]">Total</span>
              <span className="text-[var(--primary-text-color)]">{isCard
                ? `$${Number(order.totalUsd).toFixed(2)}`
                : `${Number(order.totalSech).toLocaleString("en-US", { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
              }</span>
            </div>
          </div>
          {order.notes && <DetailRow label="Notes" value={order.notes} />}
        </div>
      ) : (
        <p className="text-[var(--secondary-text-color)] text-[12px] py-1">No order data found for this transaction.</p>
      )}
    </RelatedSection>
  );
}

// ─── Copyable address (light theme) ─────────────────────────

function CopyableAddress({ address, label }: { address: string; label: string }) {
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const handleCopy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    toast({ title: "Copied", description: `${label} address copied to clipboard` });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button type="button" onClick={handleCopy} className="flex items-center gap-1.5 text-left group">
      <span className="text-[var(--primary-text-color)] text-[13px] font-medium break-all">
        {address}
      </span>
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-[var(--secondary-text-color)] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </button>
  );
}

// ─── Type badge color (light) ───────────────────────────────

const getTypeBadgeColor = (type: string): string => {
  switch (type) {
    case "order": return "bg-blue-100 text-blue-800";
    case "membership": return "bg-purple-100 text-purple-800";
    case "entry_pass": return "bg-green-100 text-green-800";
    case "mint": return "bg-yellow-100 text-yellow-800";
    case "swap": return "bg-orange-100 text-orange-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

// ─── Main dialog (light theme) ──────────────────────────────

const CANCELLABLE_TYPES = new Set(["order", "membership", "entry_pass"]);
const CANCELLABLE_STATUSES = new Set(["approved", "completed"]);

export default function AdminTransactionDetailDialog({
  transaction,
  open,
  onOpenChange,
  onRefund,
}: AdminTransactionDetailDialogProps) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const { toast } = useToast();

  if (!transaction) return null;

  const typeLabel = TRANSACTION_TYPES[transaction.type] || transaction.type;
  const isCard = transaction.paymentMethod === "CARD";
  const date = new Date(transaction.timestamp);
  const canCancel =
    CANCELLABLE_TYPES.has(transaction.type) &&
    CANCELLABLE_STATUSES.has(transaction.status ?? "");

  const handleCancel = async () => {
    setIsCancelling(true);
    try {
      const result = await cancelTransaction(transaction.id);
      toast({
        title: "Transaction cancelled",
        description: `Refund of ${result.refundTransaction.amount} ${result.refundTransaction.currency} issued.${result.txHash ? ` Tx: ${result.txHash.slice(0, 10)}...` : ""}`,
      });
      setShowCancelConfirm(false);
      onOpenChange(false);
      onRefund?.();
    } catch (err: any) {
      toast({
        title: "Cancel failed",
        description: err.message || "Something went wrong",
        variant: "destructive",
      });
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] max-h-[85vh] overflow-y-auto bg-white border-gray-200 rounded-[16px] p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${getTypeBadgeColor(transaction.type)}`}>
              <ArrowUpRight className="h-5 w-5" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-[var(--primary-text-color)] text-[16px] font-semibold">
                {typeLabel}
              </DialogTitle>
              <DialogDescription className="text-[var(--secondary-text-color)] text-[12px] mt-0.5">
                Transaction #{transaction.id}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Amount */}
        <div className="px-5 py-4 border-t border-gray-200">
          <div className="text-center">
            <span className="text-[28px] leading-[32px] font-bold text-[var(--primary-text-color)]">
              {isCard
                ? `$${parseFloat(transaction.amount.toString()).toFixed(2)}`
                : parseFloat(transaction.amount.toString()).toFixed(6)}
            </span>
            {!isCard && (
              <span className="text-[var(--secondary-text-color)] text-[14px] font-medium ml-2">
                {transaction.currency ?? "SECH"}
              </span>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="px-5 py-4 border-t border-gray-200 flex flex-col gap-4">
          {/* Date & Time */}
          <div>
            <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
              Date & Time
            </p>
            <p className="text-[var(--primary-text-color)] text-[13px] font-medium">
              {date.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              {" at "}
              {date.toLocaleTimeString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>

          {/* Type */}
          <div>
            <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
              Type
            </p>
            <span className={`inline-block text-[12px] font-medium px-2.5 py-0.5 rounded-full ${getTypeBadgeColor(transaction.type)}`}>
              {typeLabel}
            </span>
          </div>

          {/* Payment Method */}
          {transaction.paymentMethod && (
            <div>
              <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
                Payment Method
              </p>
              {isCard ? (
                transaction.paymentDetails?.last4 ? (
                  <span className="inline-flex items-center gap-2">
                    <CardBrandIcon brand={transaction.paymentDetails?.brand} width={36} />
                    <span className="text-[var(--primary-text-color)] text-[13px] font-medium">
                      ••••{transaction.paymentDetails.last4}
                    </span>
                  </span>
                ) : (
                  <span className="inline-block text-[12px] font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                    Card
                  </span>
                )
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-orange-500 shrink-0" />
                  <span className="text-[var(--primary-text-color)] text-[13px] font-medium">
                    {transaction.fromAddress
                      ? `${transaction.fromAddress.slice(0, 6)}...${transaction.fromAddress.slice(-4)}`
                      : "Token"}
                  </span>
                </span>
              )}
            </div>
          )}

          {/* Status */}
          {transaction.status && (
            <div>
              <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
                Status
              </p>
              <span className={`inline-block text-[12px] font-medium px-2.5 py-0.5 rounded-full ${
                transaction.status === 'approved' || transaction.status === 'completed'
                  ? 'bg-green-100 text-green-700'
                  : transaction.status === 'declined' || transaction.status === 'cancelled'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
              }`}>
                {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
              </span>
            </div>
          )}

          {/* From */}
          {transaction.fromAddress && (
            <div>
              <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
                From
              </p>
              <CopyableAddress address={transaction.fromAddress} label="Sender" />
            </div>
          )}

          {/* To */}
          {transaction.toAddress && (
            <div>
              <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
                To
              </p>
              <CopyableAddress address={transaction.toAddress} label="Recipient" />
            </div>
          )}

          {/* Order ID */}
          {transaction.orderId && (
            <div>
              <p className="text-[var(--secondary-text-color)] text-[11px] uppercase tracking-wider mb-1">
                Order ID
              </p>
              <p className="text-[var(--primary-text-color)] text-[13px] font-medium">
                {transaction.orderId}
              </p>
            </div>
          )}
        </div>

        {/* Related data — collapsible sections */}
        {transaction.type === "membership" && transaction.fromAddress && (
          <AdminMembershipDetails transactionId={transaction.id} walletAddress={transaction.fromAddress} />
        )}
        {transaction.type === "entry_pass" && transaction.fromAddress && (
          <AdminEntryPassDetails transactionId={transaction.id} walletAddress={transaction.fromAddress} />
        )}
        {transaction.type === "order" && transaction.orderId && (
          <AdminOrderDetails orderId={transaction.orderId} isCard={isCard} />
        )}

        {/* Footer summary */}
        {(transaction.fromAddress || transaction.toAddress) && (
          <div className="px-5 py-3 border-t border-gray-200 flex items-center justify-between">
            <span className="text-[var(--secondary-text-color)] text-[11px]">
              {transaction.fromAddress ? formatShortCryptoAddress(transaction.fromAddress) : '—'}
            </span>
            <span className="text-[var(--secondary-text-color)] text-[11px]">&rarr;</span>
            <span className="text-[var(--secondary-text-color)] text-[11px]">
              {transaction.toAddress ? formatShortCryptoAddress(transaction.toAddress) : '—'}
            </span>
          </div>
        )}

        {/* Cancel & Refund action */}
        {canCancel && (
          <div className="px-5 py-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowCancelConfirm(true)}
              disabled={isCancelling}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 transition-colors disabled:opacity-50"
            >
              {isCancelling ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Ban className="h-4 w-4" />
              )}
              Cancel & Refund
            </button>
          </div>
        )}
      </DialogContent>

      {/* Cancel confirmation dialog */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel & Refund Transaction</AlertDialogTitle>
            <AlertDialogDescription>
              This will cancel transaction #{transaction.id} and issue a full refund
              {isCard ? " to the customer's card" : " of SECH tokens to the customer's wallet"}.
              The associated {typeLabel.toLowerCase()} will also be cancelled. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCancelling}>Keep Transaction</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleCancel(); }}
              disabled={isCancelling}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isCancelling && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Cancel & Refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
